import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut, Settings, ShieldCheck, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { authService } from '@/services/auth';
import { useSession } from '@/stores/session';
import { useTheme, SKINS, type Skin } from '@/theme/useTheme';

// Fallback demo (sin backend); con sesión real se muestra el usuario autenticado.
const DEMO_USER = { name: 'Gabriela C.', org: 'Cassimmit Tax Group', initial: 'G' };

/** Muestra en miniatura de cada piel (fondo→acento). */
const SKIN_SWATCH: Record<Skin, string> = {
  aurora: 'linear-gradient(135deg, #0b1220 55%, #7C9CFF)',
  ledger: 'linear-gradient(135deg, #F7F8FB 55%, #1B44E4)',
  institutional: 'linear-gradient(135deg, #0C0F16 55%, #10B981)',
  privatebank: 'linear-gradient(135deg, #F5F4EF 55%, #122019)',
};

/** Account avatar pinned to the top-right of the topbar (approved design). */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sessionUser = useSession((s) => s.user);
  const display = sessionUser
    ? { name: sessionUser.fullName, org: sessionUser.email, initial: (sessionUser.fullName[0] ?? 'V').toUpperCase() }
    : DEMO_USER;
  const [open, setOpen] = useState(false);
  const skin = useTheme((s) => s.skin);
  const setSkin = useTheme((s) => s.setSkin);
  const rootRef = useRef<HTMLDivElement>(null);

  const signOut = async () => {
    await authService.signOut();
    useSession.getState().setUser(null);
    if (authService.isReal) navigate('/login');
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Auditoría 4-sep: todo ítem del menú lleva a un destino real.
  const items: { icon: typeof User; label: string; action?: () => void | Promise<void> }[] = [
    { icon: User, label: t('account.profile'), action: () => navigate('/settings') },
    { icon: ShieldCheck, label: t('account.security'), action: () => navigate('/mfa') },
    { icon: Settings, label: t('account.preferences'), action: () => navigate('/settings') },
    { icon: LogOut, label: t('account.signOut'), action: signOut },
  ];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('account.menu')}
        onClick={() => setOpen((v) => !v)}
        className="glass focus-ring flex items-center gap-2.5 rounded-full p-1 pr-1 sm:pr-3"
      >
        <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-brand-gradient text-sm font-bold text-white shadow-glow">
          {display.initial}
        </span>
        <span className="hidden text-left leading-tight sm:block">
          <span className="block text-xs font-semibold">{display.name}</span>
          <span className="block text-[10.5px] text-muted-foreground">{display.org}</span>
        </span>
        <ChevronDown
          className={cn(
            'hidden h-3.5 w-3.5 text-muted-foreground transition-transform sm:block',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="glass-strong absolute right-0 top-full z-50 mt-2 w-52 rounded-2xl p-1.5 animate-fade-up"
        >
          {/* Piel del UI (conmutables — decisión 27-ago; base del white-label) */}
          <div className="px-3 pb-1.5 pt-2">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t('account.appearance')}
            </p>
            <div className="flex gap-1.5" role="group" aria-label={t('account.appearance')}>
              {SKINS.map((s) => (
                <button
                  key={s}
                  type="button"
                  title={t(`account.skins.${s}`)}
                  aria-label={t(`account.skins.${s}`)}
                  aria-pressed={skin === s}
                  onClick={() => setSkin(s)}
                  className={cn(
                    'focus-ring h-7 w-7 rounded-full border-2 transition-transform hover:scale-110',
                    skin === s ? 'border-primary' : 'border-border/60',
                  )}
                  style={{ background: SKIN_SWATCH[s] }}
                />
              ))}
            </div>
          </div>
          <div className="mx-2 my-1 h-px bg-border/60" />
          {items.map(({ icon: Icon, label, action }) => (
            <button
              key={label}
              role="menuitem"
              type="button"
              onClick={() => {
                setOpen(false);
                void action?.();
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground"
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
